import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Plus, Trash2, GripVertical, Save, Download, Eye, Loader2,
  User, Briefcase, GraduationCap, Award, Code, Mail, Phone,
  MapPin, Globe, ChevronUp, ChevronDown, Sparkles
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_TYPES = {
  personal: { label: 'Personal Info', icon: User, color: 'text-blue-600' },
  summary: { label: 'Professional Summary', icon: Sparkles, color: 'text-purple-600' },
  experience: { label: 'Work Experience', icon: Briefcase, color: 'text-emerald-600' },
  education: { label: 'Education', icon: GraduationCap, color: 'text-amber-600' },
  skills: { label: 'Skills', icon: Code, color: 'text-red-600' },
  certifications: { label: 'Certifications', icon: Award, color: 'text-indigo-600' },
};

const defaultCV = {
  personal: { full_name: '', email: '', phone: '', location: '', linkedin: '', website: '' },
  summary: '',
  experience: [{ id: 1, company: '', title: '', start_date: '', end_date: '', current: false, description: '' }],
  education: [{ id: 1, institution: '', degree: '', field: '', start_year: '', end_year: '', grade: '' }],
  skills: [],
  certifications: [{ id: 1, name: '', issuer: '', year: '' }],
};

export default function CVBuilderPage() {
  const { user, token, api } = useAuth();
  const [cv, setCV] = useState(defaultCV);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [sectionOrder, setSectionOrder] = useState(['personal', 'summary', 'experience', 'education', 'skills', 'certifications']);

  useEffect(() => { loadCV(); }, []);

  const loadCV = async () => {
    try {
      const res = await api.get('/cv-data');
      if (res.data && Object.keys(res.data).length > 2) {
        const loaded = {
          personal: res.data.personal_info || defaultCV.personal,
          summary: res.data.summary || '',
          experience: (res.data.experience || []).length > 0 ? res.data.experience : defaultCV.experience,
          education: (res.data.education || []).length > 0 ? res.data.education : defaultCV.education,
          skills: res.data.skills || [],
          certifications: (res.data.certifications || []).length > 0 ? res.data.certifications : defaultCV.certifications,
        };
        setCV(loaded);
      }
    } catch {}
  };

  const saveCV = async () => {
    setSaving(true);
    try {
      await api.put('/cv-data', {
        personal_info: cv.personal,
        summary: cv.summary,
        experience: cv.experience,
        education: cv.education,
        skills: cv.skills,
        certifications: cv.certifications,
      });
      toast.success('CV saved successfully!');
    } catch { toast.error('Failed to save CV'); }
    finally { setSaving(false); }
  };

  const updatePersonal = (field, value) => {
    setCV(prev => ({ ...prev, personal: { ...prev.personal, [field]: value } }));
  };

  const addExperience = () => {
    setCV(prev => ({
      ...prev,
      experience: [...prev.experience, { id: Date.now(), company: '', title: '', start_date: '', end_date: '', current: false, description: '' }]
    }));
  };

  const updateExperience = (id, field, value) => {
    setCV(prev => ({
      ...prev,
      experience: prev.experience.map(e => e.id === id ? { ...e, [field]: value } : e)
    }));
  };

  const removeExperience = (id) => {
    setCV(prev => ({ ...prev, experience: prev.experience.filter(e => e.id !== id) }));
  };

  const addEducation = () => {
    setCV(prev => ({
      ...prev,
      education: [...prev.education, { id: Date.now(), institution: '', degree: '', field: '', start_year: '', end_year: '', grade: '' }]
    }));
  };

  const updateEducation = (id, field, value) => {
    setCV(prev => ({
      ...prev,
      education: prev.education.map(e => e.id === id ? { ...e, [field]: value } : e)
    }));
  };

  const removeEducation = (id) => {
    setCV(prev => ({ ...prev, education: prev.education.filter(e => e.id !== id) }));
  };

  const addSkill = () => {
    if (!newSkill.trim()) return;
    setCV(prev => ({ ...prev, skills: [...(prev.skills || []), newSkill.trim()] }));
    setNewSkill('');
  };

  const removeSkill = (index) => {
    setCV(prev => ({ ...prev, skills: prev.skills.filter((_, i) => i !== index) }));
  };

  const addCertification = () => {
    setCV(prev => ({
      ...prev,
      certifications: [...prev.certifications, { id: Date.now(), name: '', issuer: '', year: '' }]
    }));
  };

  const updateCertification = (id, field, value) => {
    setCV(prev => ({
      ...prev,
      certifications: prev.certifications.map(c => c.id === id ? { ...c, [field]: value } : c)
    }));
  };

  const removeCertification = (id) => {
    setCV(prev => ({ ...prev, certifications: prev.certifications.filter(c => c.id !== id) }));
  };

  const moveSection = (index, direction) => {
    const newOrder = [...sectionOrder];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newOrder.length) return;
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setSectionOrder(newOrder);
  };

  const generateAISummary = async () => {
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/generate-summary`, {
        experience: cv.experience,
        skills: cv.skills,
        education: cv.education,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.summary) {
        setCV(prev => ({ ...prev, summary: res.data.summary }));
        toast.success('AI summary generated!');
      }
    } catch {
      const expText = cv.experience.map(e => `${e.title} at ${e.company}`).filter(t => t !== ' at ').join(', ');
      const skillsText = (cv.skills || []).join(', ');
      const fallback = `Experienced professional${expText ? ` with background in ${expText}` : ''}${skillsText ? `. Skilled in ${skillsText}` : ''}. Looking for new opportunities to contribute and grow.`;
      setCV(prev => ({ ...prev, summary: fallback }));
      toast.info('Generated basic summary (AI service unavailable)');
    } finally { setAiGenerating(false); }
  };

  const renderSection = (sectionId) => {
    const config = SECTION_TYPES[sectionId];
    const Icon = config.icon;

    switch (sectionId) {
      case 'personal':
        return (
          <Card key={sectionId} data-testid="cv-section-personal">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${config.color}`} />
                <CardTitle className="text-lg">{config.label}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Full Name</Label><Input value={cv.personal.full_name} onChange={e => updatePersonal('full_name', e.target.value)} placeholder="John Smith" data-testid="cv-fullname" /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={cv.personal.email} onChange={e => updatePersonal('email', e.target.value)} placeholder="john@example.com" /></div>
                <div className="space-y-2"><Label>Phone</Label><Input value={cv.personal.phone} onChange={e => updatePersonal('phone', e.target.value)} placeholder="+44 7700 900123" /></div>
                <div className="space-y-2"><Label>Location</Label><Input value={cv.personal.location} onChange={e => updatePersonal('location', e.target.value)} placeholder="London, UK" /></div>
                <div className="space-y-2"><Label>LinkedIn</Label><Input value={cv.personal.linkedin} onChange={e => updatePersonal('linkedin', e.target.value)} placeholder="linkedin.com/in/johnsmith" /></div>
                <div className="space-y-2"><Label>Website</Label><Input value={cv.personal.website} onChange={e => updatePersonal('website', e.target.value)} placeholder="johnsmith.com" /></div>
              </div>
            </CardContent>
          </Card>
        );

      case 'summary':
        return (
          <Card key={sectionId} data-testid="cv-section-summary">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${config.color}`} />
                  <CardTitle className="text-lg">{config.label}</CardTitle>
                </div>
                <Button size="sm" variant="outline" onClick={generateAISummary} disabled={aiGenerating} data-testid="ai-summary-btn">
                  {aiGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  AI Generate
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea value={cv.summary} onChange={e => setCV(prev => ({ ...prev, summary: e.target.value }))} rows={4} placeholder="A brief summary of your professional background and career goals..." data-testid="cv-summary" />
            </CardContent>
          </Card>
        );

      case 'experience':
        return (
          <Card key={sectionId} data-testid="cv-section-experience">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${config.color}`} />
                  <CardTitle className="text-lg">{config.label}</CardTitle>
                </div>
                <Button size="sm" variant="outline" onClick={addExperience} data-testid="add-experience-btn">
                  <Plus className="w-4 h-4 mr-2" />Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {cv.experience.map((exp, idx) => (
                <div key={exp.id} className="border rounded-lg p-4 relative">
                  <Button variant="ghost" size="icon" className="absolute top-2 right-2 text-red-500 hover:text-red-700" onClick={() => removeExperience(exp.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-10">
                    <div className="space-y-2"><Label>Job Title</Label><Input value={exp.title} onChange={e => updateExperience(exp.id, 'title', e.target.value)} placeholder="Software Engineer" /></div>
                    <div className="space-y-2"><Label>Company</Label><Input value={exp.company} onChange={e => updateExperience(exp.id, 'company', e.target.value)} placeholder="TechCorp" /></div>
                    <div className="space-y-2"><Label>Start Date</Label><Input type="month" value={exp.start_date} onChange={e => updateExperience(exp.id, 'start_date', e.target.value)} /></div>
                    <div className="space-y-2"><Label>End Date</Label><Input type="month" value={exp.end_date} onChange={e => updateExperience(exp.id, 'end_date', e.target.value)} disabled={exp.current} placeholder={exp.current ? 'Present' : ''} /></div>
                  </div>
                  <div className="flex items-center gap-2 mt-2 mb-3">
                    <input type="checkbox" checked={exp.current} onChange={e => updateExperience(exp.id, 'current', e.target.checked)} className="rounded" />
                    <Label className="text-sm">Currently working here</Label>
                  </div>
                  <div className="space-y-2"><Label>Description</Label><Textarea value={exp.description} onChange={e => updateExperience(exp.id, 'description', e.target.value)} rows={3} placeholder="Key achievements and responsibilities..." /></div>
                </div>
              ))}
            </CardContent>
          </Card>
        );

      case 'education':
        return (
          <Card key={sectionId} data-testid="cv-section-education">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${config.color}`} />
                  <CardTitle className="text-lg">{config.label}</CardTitle>
                </div>
                <Button size="sm" variant="outline" onClick={addEducation} data-testid="add-education-btn">
                  <Plus className="w-4 h-4 mr-2" />Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {cv.education.map((edu) => (
                <div key={edu.id} className="border rounded-lg p-4 relative">
                  <Button variant="ghost" size="icon" className="absolute top-2 right-2 text-red-500 hover:text-red-700" onClick={() => removeEducation(edu.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-10">
                    <div className="space-y-2"><Label>Institution</Label><Input value={edu.institution} onChange={e => updateEducation(edu.id, 'institution', e.target.value)} placeholder="University of London" /></div>
                    <div className="space-y-2"><Label>Degree</Label><Input value={edu.degree} onChange={e => updateEducation(edu.id, 'degree', e.target.value)} placeholder="BSc Computer Science" /></div>
                    <div className="space-y-2"><Label>Field of Study</Label><Input value={edu.field} onChange={e => updateEducation(edu.id, 'field', e.target.value)} placeholder="Computer Science" /></div>
                    <div className="space-y-2"><Label>Grade</Label><Input value={edu.grade} onChange={e => updateEducation(edu.id, 'grade', e.target.value)} placeholder="First Class Honours" /></div>
                    <div className="space-y-2"><Label>Start Year</Label><Input value={edu.start_year} onChange={e => updateEducation(edu.id, 'start_year', e.target.value)} placeholder="2018" /></div>
                    <div className="space-y-2"><Label>End Year</Label><Input value={edu.end_year} onChange={e => updateEducation(edu.id, 'end_year', e.target.value)} placeholder="2022" /></div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        );

      case 'skills':
        return (
          <Card key={sectionId} data-testid="cv-section-skills">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${config.color}`} />
                <CardTitle className="text-lg">{config.label}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {(cv.skills || []).map((skill, idx) => (
                  <Badge key={idx} variant="secondary" className="pl-3 pr-1 py-1.5 text-sm flex items-center gap-1">
                    {skill}
                    <button onClick={() => removeSkill(idx)} className="ml-1 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newSkill} onChange={e => setNewSkill(e.target.value)} placeholder="Add a skill (e.g. Python, React, Project Management)" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())} data-testid="skill-input" />
                <Button onClick={addSkill} variant="outline" data-testid="add-skill-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        );

      case 'certifications':
        return (
          <Card key={sectionId} data-testid="cv-section-certifications">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${config.color}`} />
                  <CardTitle className="text-lg">{config.label}</CardTitle>
                </div>
                <Button size="sm" variant="outline" onClick={addCertification}>
                  <Plus className="w-4 h-4 mr-2" />Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {cv.certifications.map((cert) => (
                <div key={cert.id} className="border rounded-lg p-4 relative">
                  <Button variant="ghost" size="icon" className="absolute top-2 right-2 text-red-500 hover:text-red-700" onClick={() => removeCertification(cert.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pr-10">
                    <div className="space-y-2"><Label>Certification</Label><Input value={cert.name} onChange={e => updateCertification(cert.id, 'name', e.target.value)} placeholder="AWS Solutions Architect" /></div>
                    <div className="space-y-2"><Label>Issuing Organisation</Label><Input value={cert.issuer} onChange={e => updateCertification(cert.id, 'issuer', e.target.value)} placeholder="Amazon Web Services" /></div>
                    <div className="space-y-2"><Label>Year</Label><Input value={cert.year} onChange={e => updateCertification(cert.id, 'year', e.target.value)} placeholder="2024" /></div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  if (preview) {
    return (
      <div className="min-h-screen bg-white" data-testid="cv-preview">
        <div className="sticky top-0 bg-white border-b z-50 py-3 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <h2 className="font-bold text-lg">CV Preview</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPreview(false)}>Back to Editor</Button>
              <Button onClick={() => window.print()} className="bg-[#003366]"><Download className="w-4 h-4 mr-2" />Download PDF</Button>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-8 print:p-0">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-[#003366]">{cv.personal.full_name || 'Your Name'}</h1>
            <div className="flex flex-wrap justify-center gap-4 mt-2 text-sm text-muted-foreground">
              {cv.personal.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{cv.personal.email}</span>}
              {cv.personal.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{cv.personal.phone}</span>}
              {cv.personal.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{cv.personal.location}</span>}
              {cv.personal.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{cv.personal.website}</span>}
            </div>
          </div>
          {cv.summary && (
            <div className="mb-6"><h2 className="text-lg font-bold text-[#003366] border-b-2 border-[#003366] pb-1 mb-3">Professional Summary</h2><p className="text-sm leading-relaxed">{cv.summary}</p></div>
          )}
          {cv.experience.some(e => e.title || e.company) && (
            <div className="mb-6"><h2 className="text-lg font-bold text-[#003366] border-b-2 border-[#003366] pb-1 mb-3">Work Experience</h2>
              {cv.experience.filter(e => e.title || e.company).map(exp => (
                <div key={exp.id} className="mb-4">
                  <div className="flex justify-between"><h3 className="font-semibold">{exp.title}</h3><span className="text-sm text-muted-foreground">{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</span></div>
                  <p className="text-sm text-[#003366] font-medium">{exp.company}</p>
                  {exp.description && <p className="text-sm mt-1 leading-relaxed whitespace-pre-line">{exp.description}</p>}
                </div>
              ))}
            </div>
          )}
          {cv.education.some(e => e.institution) && (
            <div className="mb-6"><h2 className="text-lg font-bold text-[#003366] border-b-2 border-[#003366] pb-1 mb-3">Education</h2>
              {cv.education.filter(e => e.institution).map(edu => (
                <div key={edu.id} className="mb-3">
                  <div className="flex justify-between"><h3 className="font-semibold">{edu.degree}</h3><span className="text-sm text-muted-foreground">{edu.start_year} - {edu.end_year}</span></div>
                  <p className="text-sm text-[#003366]">{edu.institution}</p>
                  {edu.grade && <p className="text-sm text-muted-foreground">{edu.grade}</p>}
                </div>
              ))}
            </div>
          )}
          {(cv.skills || []).length > 0 && (
            <div className="mb-6"><h2 className="text-lg font-bold text-[#003366] border-b-2 border-[#003366] pb-1 mb-3">Skills</h2>
              <div className="flex flex-wrap gap-2">{cv.skills.map((s, i) => <Badge key={i} variant="secondary">{s}</Badge>)}</div>
            </div>
          )}
          {cv.certifications.some(c => c.name) && (
            <div className="mb-6"><h2 className="text-lg font-bold text-[#003366] border-b-2 border-[#003366] pb-1 mb-3">Certifications</h2>
              {cv.certifications.filter(c => c.name).map(cert => (
                <div key={cert.id} className="mb-2"><span className="font-medium">{cert.name}</span> - <span className="text-sm text-muted-foreground">{cert.issuer} ({cert.year})</span></div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="cv-builder-page">
      <div className="bg-[#003366] text-white py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">CV Builder</h1>
              <p className="text-white/80">Build a professional CV with our easy-to-use builder</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => setPreview(true)} data-testid="preview-cv-btn">
                <Eye className="w-4 h-4 mr-2" />Preview
              </Button>
              <Button onClick={saveCV} disabled={saving} className="bg-[#E11D48] hover:bg-[#E11D48]/90" data-testid="save-cv-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save CV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {sectionOrder.map((sectionId, index) => (
          <div key={sectionId} className="relative">
            <div className="absolute -left-12 top-4 hidden xl:flex flex-col gap-1">
              <button onClick={() => moveSection(index, -1)} className="p-1 rounded hover:bg-gray-200" disabled={index === 0}><ChevronUp className="w-4 h-4" /></button>
              <GripVertical className="w-4 h-4 text-gray-400 mx-auto" />
              <button onClick={() => moveSection(index, 1)} className="p-1 rounded hover:bg-gray-200" disabled={index === sectionOrder.length - 1}><ChevronDown className="w-4 h-4" /></button>
            </div>
            {renderSection(sectionId)}
          </div>
        ))}
      </div>
    </div>
  );
}
